// Wolt / Bolt Food sifarişlərinin qəbul paneli — bütün POS səhifələrində.
//
// Platforma sifarişi pos_local-a `open` statusunda, MƏTBƏXƏ GÖNDƏRİLMƏDƏN
// düşür. Kassir burada:
//   • tərkibə baxır, hazırlıq müddətini seçib QƏBUL edir → mətbəxə gedir,
//     KOT çap olunur, platformaya "qəbul etdik, N dəqiqəyə hazır" gedir;
//   • və ya səbəb göstərib RƏDD edir → platformaya xəbər gedir;
//   • yemək hazır olanda "Hazırdır" basır → kuryer çağırılır.
//
// Gözləyən sifariş olduqca səs təkrar çalır (Wolt planşeti kimi) — gecikmiş
// qəbul platformada avtomatik ləğvə və restoranın reytinqinin düşməsinə gətirir.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bike, Check, ChefHat, Clock, Volume2, VolumeX, X } from 'lucide-react';
import { POS_ENABLED } from '../../api/pos/config';
import { getPosWs } from '../../api/pos/posWs';
import {
  REJECT_REASONS,
  acceptDeliveryOrder,
  listDeliveryOrders,
  markDeliveryOrderReady,
  prepChoices,
  providerLabel,
  rejectDeliveryOrder,
  secondsLeft,
  DeliveryGatewayError,
  type DeliveryOrder,
  type RejectReason,
} from '../../api/pos/deliveryGateway';
import { useI18n } from '../../i18n/I18nContext';
import { usePermissions } from '../../context/PermissionsContext';
import notify from '../ui/notify';
import soundFile from '../../assets/sound.mp3';

/** WS olmasa da (kabel çıxıb, pos_local yenidən başlayır) siyahı təzələnsin. */
const FALLBACK_POLL_MS = 30_000;
const MUTE_KEY = 'delivery_panel_muted';

const PROVIDER_STYLE: Record<string, string> = {
  wolt: 'bg-sky-500 text-white',
  bolt: 'bg-emerald-500 text-white',
};

function formatMoney(cents: number): string {
  return `${(cents / 100).toFixed(2)} ₼`;
}

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function DeliveryOrdersPanel(): JSX.Element | null {
  const { t } = useI18n();
  const { canAccessRoute } = usePermissions() as { canAccessRoute: (p: string) => boolean };
  // Sifarişlər səhifəsinə girişi olan hər kəs (kassir, menecer) platforma
  // sifarişini idarə edə bilər. Aşpaz/ofisant terminalında panel görünmür.
  const allowed = POS_ENABLED && canAccessRoute('/siparisler');

  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState<string | null>(null);
  const [prep, setPrep] = useState<Record<string, number>>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [muted, setMuted] = useState(readMuted);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const knownIncoming = useRef<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const list = await listDeliveryOrders();
      setOrders(list);

      // Yeni gələn sifariş: paneli aç və qısa bildiriş göstər.
      const fresh = list.filter((o) => o.status === 'incoming' && !knownIncoming.current.has(o.orderId));
      knownIncoming.current = new Set(list.filter((o) => o.status === 'incoming').map((o) => o.orderId));
      if (fresh.length > 0) {
        setOpen(true);
        for (const o of fresh) {
          notify.alert(t('delivery.newOrder', { provider: providerLabel(o.provider), id: o.displayId ?? o.cloudOrderId }));
        }
      }
    } catch {
      // pos_local əlçatmazdır — köhnə siyahı qalır, növbəti cəhd gələcək.
    }
  }, [t]);

  // İlk yükləmə + WS + ehtiyat sorğu.
  useEffect(() => {
    if (!allowed) return;
    void refresh();

    const ws = getPosWs();
    const release = ws.acquire();
    const off = ws.onEvent((e) => {
      if (e.type === 'delivery.OrderCancelled') {
        const p = (e.payload ?? {}) as { provider?: string; displayId?: string; cloudOrderId?: string; wasInKitchen?: boolean };
        notify.warning(
          <div>
            <div className="font-bold">
              {t('delivery.cancelledToast', { provider: providerLabel(p.provider ?? ''), id: p.displayId ?? p.cloudOrderId ?? '' })}
            </div>
            {p.wasInKitchen && <div className="text-sm">{t('delivery.cancelledKitchen')}</div>}
          </div>,
          { autoClose: p.wasInKitchen ? false : 8000 },
        );
      }
      if (e.type.startsWith('delivery.') || e.type === 'orders.OrderSentToKitchen' || e.type === 'orders.OrderVoided') {
        void refresh();
      }
    });
    const poll = setInterval(() => void refresh(), FALLBACK_POLL_MS);

    return () => {
      off();
      release();
      clearInterval(poll);
    };
  }, [allowed, refresh]);

  const incoming = useMemo(() => orders.filter((o) => o.status === 'incoming'), [orders]);

  // Geri sayım yalnız gözləyən sifariş olanda işləyir.
  useEffect(() => {
    if (incoming.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [incoming.length]);

  // Gözləyən sifariş olduqca səs təkrarlanır.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (incoming.length > 0 && !muted) {
      audio.play().catch(() => {
        /* brauzer avtomatik səsi blokladı — ilk toxunuşdan sonra çalacaq */
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [incoming.length, muted]);

  const toggleMute = () => {
    setMuted((m) => {
      try {
        localStorage.setItem(MUTE_KEY, m ? '0' : '1');
      } catch {
        /* noop */
      }
      return !m;
    });
  };

  const run = async (orderId: string, action: () => Promise<unknown>, success: string) => {
    setBusy(orderId);
    try {
      await action();
      notify.success(success);
      setRejecting(null);
    } catch (e) {
      if (e instanceof DeliveryGatewayError && (e.code === 'DELIVERY_NOT_INCOMING' || e.code === 'DELIVERY_NOT_ACCEPTED')) {
        notify.warning(t('delivery.err.notIncoming'));
      } else {
        notify.error(t('delivery.err.generic', { msg: e instanceof Error ? e.message : String(e) }));
      }
    } finally {
      setBusy(null);
      void refresh();
    }
  };

  if (!allowed) return null;

  return (
    <>
      <audio ref={audioRef} src={soundFile} preload="auto" loop />

      {/* Üzən düymə — aktiv sifariş olmasa gizlidir. */}
      {orders.length > 0 && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`fixed bottom-20 right-5 z-[9990] flex items-center gap-2 px-4 py-3 rounded-2xl shadow-2xl font-black text-white ${
            incoming.length > 0 ? 'bg-red-600 animate-pulse' : 'bg-gray-900 dark:bg-white/10'
          }`}
        >
          <Bike className="w-5 h-5" />
          {t('delivery.button')}
          <span className="min-w-[1.5rem] h-6 px-1.5 rounded-full bg-white text-gray-900 text-sm flex items-center justify-center">
            {incoming.length > 0 ? incoming.length : orders.length}
          </span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[9995] flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-full max-w-md h-full bg-white dark:bg-[#111318] shadow-2xl flex flex-col">
            <header className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-white/[0.06]">
              <Bike className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              <h2 className="flex-1 font-black text-lg text-gray-900 dark:text-white">{t('delivery.title')}</h2>
              <button
                type="button"
                onClick={toggleMute}
                title={muted ? t('delivery.unmute') : t('delivery.mute')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {orders.length === 0 && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-10">{t('delivery.empty')}</p>
              )}

              {orders.map((o) => {
                const left = o.status === 'incoming' ? secondsLeft(o, now) : null;
                const choices = prepChoices(o);
                const chosen = prep[o.orderId] ?? o.defaultPrep ?? 15;
                const isBusy = busy === o.orderId;

                return (
                  <article
                    key={o.orderId}
                    className={`rounded-2xl border p-4 space-y-3 ${
                      o.status === 'incoming'
                        ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10'
                        : 'border-gray-200 dark:border-white/[0.08]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-black uppercase ${
                          PROVIDER_STYLE[o.provider] ?? 'bg-gray-700 text-white'
                        }`}
                      >
                        {providerLabel(o.provider)}
                      </span>
                      <span className="font-black text-gray-900 dark:text-white">#{o.displayId ?? o.cloudOrderId}</span>
                      <span className="ml-auto text-xs font-bold text-gray-500 dark:text-gray-400">
                        {t(`delivery.status.${o.status}`)}
                      </span>
                    </div>

                    {o.customerName && (
                      <div className="text-sm text-gray-600 dark:text-gray-300">{o.customerName}</div>
                    )}
                    {o.scheduledFor && (
                      <div className="text-sm font-bold text-purple-600 dark:text-purple-300">
                        {t('delivery.scheduled', { time: new Date(o.scheduledFor).toLocaleString() })}
                      </div>
                    )}

                    <ul className="text-sm space-y-1">
                      {o.items.map((it, idx) => (
                        <li key={idx} className="text-gray-800 dark:text-gray-200">
                          <span className="font-bold">{it.quantity}×</span> {it.name}
                          {it.modifiers && <div className="pl-5 text-xs text-gray-500">+ {it.modifiers}</div>}
                          {it.notes && <div className="pl-5 text-xs italic text-orange-600">{it.notes}</div>}
                        </li>
                      ))}
                    </ul>

                    <div className="flex justify-between text-sm font-bold text-gray-900 dark:text-white">
                      <span>{t('delivery.total')}</span>
                      <span>{formatMoney(o.platformTotalCents)}</span>
                    </div>

                    {o.status === 'incoming' && (
                      <>
                        {left !== null && (
                          <div
                            className={`flex items-center gap-2 text-sm font-black ${
                              left > 60 ? 'text-gray-700 dark:text-gray-200' : 'text-red-600'
                            }`}
                          >
                            <Clock className="w-4 h-4" />
                            {left > 0 ? t('delivery.timeLeft', { time: formatClock(left) }) : t('delivery.expired')}
                          </div>
                        )}

                        <div>
                          <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                            {t('delivery.prepTime')}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {choices.map((n) => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setPrep((p) => ({ ...p, [o.orderId]: n }))}
                                className={`px-3 py-2 rounded-xl text-sm font-bold border ${
                                  chosen === n
                                    ? 'bg-orange-600 border-orange-600 text-white'
                                    : 'border-gray-200 dark:border-white/[0.1] text-gray-700 dark:text-gray-200'
                                }`}
                              >
                                {t('delivery.minutes', { n })}
                              </button>
                            ))}
                          </div>
                        </div>

                        {rejecting === o.orderId ? (
                          <div className="space-y-2">
                            <div className="text-xs font-bold uppercase tracking-wide text-gray-500">
                              {t('delivery.rejectReason')}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {REJECT_REASONS.map((r: RejectReason) => (
                                <button
                                  key={r}
                                  type="button"
                                  disabled={isBusy}
                                  onClick={() =>
                                    run(o.orderId, () => rejectDeliveryOrder(o.orderId, r), t('delivery.rejected'))
                                  }
                                  className="px-3 py-2 rounded-xl text-sm font-bold bg-red-600 text-white disabled:opacity-50"
                                >
                                  {t(`delivery.reason.${r}`)}
                                </button>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() => setRejecting(null)}
                              className="w-full py-2 text-sm font-bold text-gray-500"
                            >
                              <X className="inline w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setRejecting(o.orderId)}
                              className="px-4 py-3 rounded-xl font-bold border border-red-300 text-red-600 disabled:opacity-50"
                            >
                              {t('delivery.reject')}
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() =>
                                run(o.orderId, () => acceptDeliveryOrder(o.orderId, chosen), t('delivery.accepted'))
                              }
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-black bg-green-600 text-white disabled:opacity-50"
                            >
                              <Check className="w-5 h-5" />
                              {t('delivery.accept')}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {o.status === 'accepted' && (
                      <div className="flex items-center gap-2">
                        {o.prepMinutes && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {t('delivery.readyAt', {
                              n: Math.max(0, Math.round((o.updatedAt + o.prepMinutes * 60_000 - now) / 60_000)),
                            })}
                          </span>
                        )}
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => run(o.orderId, () => markDeliveryOrderReady(o.orderId), t('delivery.ready'))}
                          className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-xl font-black bg-orange-600 text-white disabled:opacity-50"
                        >
                          <ChefHat className="w-5 h-5" />
                          {t('delivery.ready')}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export default DeliveryOrdersPanel;
